// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Charge } from '@/billing/api/types';
import { SerializableCurrency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { toSerializableCurrency } from '@/currency/utilities/to-form-value';
import {
  getAmountPrecisionMessage,
  parseCurrencyAmount,
} from '@/currency/utilities/validate';
import { MilestonePhasesInput } from '@/project/components/billing-plan-constants';
import { useProjectLineItemCharges } from '@/project/hooks/use-project-line-item-charges';
import { computeConfigHash } from '@/project/utilities/billing-config-hash';
import {
  ChargeFormValue,
  ChargeMode,
  chargeFieldsMatch,
  generateCharges,
  reconcileChargeIds,
  splitCharge,
} from '@/project/utilities/generate-charges';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface ChargeScheduleConfig {
  projectKey: string;
  lineItemKey: string | null;
  isOpen: boolean;
  lineItemStartOn: DateTime | null;
  lineItemEndOn: DateTime | null;
  pricingMode: string;
  chargeSchedule: string | null;
  chargeFrequency: string | null;
  invoiceFrequency: string | null;
  invoiceMode: string | null;
  quantity: number;
  unitPriceNum: number;
  fixedPriceNum: number;
  totalOverrideNum: number | null;
  milestonePhases: MilestonePhasesInput | null;
  chargeMode: ChargeMode;
  // Required when chargeSchedule is PERIODIC; the drawer defaults this to
  // lineItemStartOn when the line item has no stored activation date yet.
  activationDate: DateTime<boolean> | null;
  // The line item's single currency, stamped onto every generated charge's
  // unitPrice / totalPrice.
  currencyCode: IsoCurrencyCode;
}

/** A charge whose amount its currency cannot carry, with the reason. */
interface UnrepresentableCharge {
  charge: ChargeFormValue;
  message: string;
}

function toChargeAmountPrecisionMessage(
  value: SerializableCurrency | null
): string | null {
  if (value === null) {
    return null;
  }

  const amount = parseCurrencyAmount(value.amount);
  return amount === null
    ? null
    : getAmountPrecisionMessage(amount, value.isoCode);
}

// Finds the first charge with an amount finer than its currency's minor unit,
// such as cents kept after the line item switched to a zero-decimal currency.
function findUnrepresentableCharge(
  charges: ChargeFormValue[]
): UnrepresentableCharge | null {
  for (const charge of charges) {
    const message =
      toChargeAmountPrecisionMessage(charge.totalPrice) ??
      toChargeAmountPrecisionMessage(charge.unitPrice);
    if (message !== null) {
      return { charge, message };
    }
  }

  return null;
}

function dateKey(value: DateTime<boolean> | null) {
  return value?.toISODate() ?? null;
}

function isUsableActivationDate(
  value: DateTime<boolean> | null
): value is DateTime {
  return value !== null && dateKey(value) !== null;
}

function configHashFromState(config: ChargeScheduleConfig): string {
  return computeConfigHash(
    config.pricingMode,
    config.chargeSchedule ?? 'PERIODIC',
    config.chargeFrequency ?? 'MONTHLY',
    config.invoiceFrequency ?? 'MONTHLY',
    config.invoiceMode ?? 'ADVANCE',
    config.quantity,
    config.unitPriceNum,
    config.fixedPriceNum,
    config.totalOverrideNum,
    config.chargeMode,
    dateKey(config.activationDate) ?? ''
  );
}

/**
 * Returns true when every server-loaded charge satisfies the
 * line-item pricing-mode invariants. Narrowly scoped to the rules
 * that genuinely cannot disagree without legacy data corruption —
 * not a structural regenerate-and-compare, which over-fires on
 * routine BE formatting variation.
 *
 *   - charge.pricingMode === line item.pricingMode
 *   - FIXED ⇒ charge.unitPrice is null
 *   - PER_UNIT (qty > 0) ⇒ charge.unitPrice is non-null
 *
 * Other field drift (quantity, prices, schedule, frequencies) is
 * already covered by the configHash-vs-generationHash signal once
 * the user edits the form. This load-time check exists only to
 * surface a stored line-item-vs-charge divergence that the form
 * drift signal can't see (the form reseeds from the line item on
 * open, so configHash matches generationHash even when bound
 * charges contradict the line item — the chrg_1xnn12nd4rcyp40z
 * scenario: line item FIXED, bound charge PER_UNIT $25k).
 */
function loadedChargesMatchFormConfig(
  localCharges: ChargeFormValue[],
  config: ChargeScheduleConfig
): boolean {
  for (const c of localCharges) {
    if (c.pricingMode !== config.pricingMode) return false;
    if (c.pricingMode === 'FIXED' && c.unitPrice !== null) return false;
    if (
      c.pricingMode === 'PER_UNIT' &&
      c.quantity > 0 &&
      c.unitPrice === null
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Stable string fingerprint of the current milestone-phases wrapper. The
 * form's useWatch subscription on `milestonePhases` yields a new object
 * reference on every nested edit, so any useCallback / useEffect that
 * captures the raw value will re-run on every keystroke. The fingerprint
 * collapses object-identity changes: when the serialized content is the
 * same (field-for-field), the hash is the same.
 */
function milestonePhasesFingerprint(
  phases: MilestonePhasesInput | null
): string {
  if (phases === null) return '';
  return JSON.stringify(phases);
}

export function useChargeScheduleState(config: ChargeScheduleConfig) {
  const {
    projectKey,
    lineItemKey,
    isOpen,
    lineItemStartOn,
    lineItemEndOn,
    pricingMode,
    chargeSchedule,
    chargeFrequency,
    invoiceFrequency,
    invoiceMode,
    quantity,
    unitPriceNum,
    fixedPriceNum,
    totalOverrideNum,
    milestonePhases,
    chargeMode,
    activationDate,
    currencyCode,
  } = config;

  const configHash = configHashFromState(config);
  // Produce a stable reference for milestonePhases that only rotates when
  // the content actually changes. useWatch returns a fresh object on
  // every nested edit, so using the raw value as a dep rebuilds downstream
  // callbacks on every keystroke — which defeats ChargeBillDateGroup
  // memoization. The cache keyed on the fingerprint collapses identity
  // churn: same content ⇒ same reference. Synchronous assignment during
  // render is safe because we compare-then-swap, matching the documented
  // useRef-as-cache pattern.
  const milestonePhasesKey = milestonePhasesFingerprint(milestonePhases);
  const stablePhasesCache = useRef<{
    key: string;
    value: MilestonePhasesInput | null;
  }>({ key: milestonePhasesKey, value: milestonePhases });
  if (stablePhasesCache.current.key !== milestonePhasesKey) {
    stablePhasesCache.current = {
      key: milestonePhasesKey,
      value: milestonePhases,
    };
  }
  const stableMilestonePhases = stablePhasesCache.current.value;

  const [localCharges, setLocalCharges] = useState<ChargeFormValue[]>([]);
  const [chargesModified, setChargesModified] = useState(false);
  const [chargesLoaded, setChargesLoaded] = useState(false);
  const [isChargeEditing, setIsChargeEditing] = useState(false);
  const [generationKey, setGenerationKey] = useState(0);
  const [generationHash, setGenerationHash] = useState<string | null>(null);
  // True when the server-loaded charges disagree with what the current
  // form config would have generated — pre-existing divergence between
  // the line item and its bound charges (e.g. line item FIXED but
  // charge stored as PER_UNIT). Independent of `generationHash`-based
  // form drift, so it survives unrelated form edits and clears only on
  // explicit Regenerate (or when the line item / drawer resets).
  const [loadedChargesAreStale, setLoadedChargesAreStale] = useState(false);
  // Baseline currency for restamp detection. Re-seeded whenever charges are
  // not loaded (initial open / line item switch) so switching line items
  // never looks like a user currency change.
  const prevCurrencyCodeRef = useRef<IsoCurrencyCode>(currencyCode);
  // Ref so clearing chargesModified on save doesn't re-fire the sync.
  const chargesModifiedRef = useRef(chargesModified);
  chargesModifiedRef.current = chargesModified;

  const chargesQuery = useProjectLineItemCharges(
    projectKey,
    isOpen ? (lineItemKey ?? null) : null
  );

  // Sync server charges to local state when loaded (and not locally modified)
  useEffect(() => {
    if (chargesQuery.data && !chargesModifiedRef.current) {
      setChargesLoaded(true);
      setLocalCharges(
        chargesQuery.data.data.map(
          (c: Charge): ChargeFormValue => ({
            id: c.id,
            key: c.key,
            label: c.label,
            pricingMode: c.pricingMode,
            quantity: c.quantity,
            unitPrice:
              c.unitPrice === null ? null : toSerializableCurrency(c.unitPrice),
            totalPrice: toSerializableCurrency(c.totalPrice),
            servicePeriodStart: c.servicePeriodStart,
            servicePeriodEnd: c.servicePeriodEnd,
            expectedBillableDate: c.expectedBillableDate,
            billingEligibleDate: c.billingEligibleDate,
            status: c.status,
            isCustomModified: c.isCustomModified,
            triggerType: c.triggerType,
            sequence: c.sequence,
          })
        )
      );
    }
  }, [chargesQuery.data]);

  // The load-time stale check reads the full form config, but it must
  // only fire on the load transition — after that, subsequent form
  // edits are caught by the configHash-vs-generationHash signal below.
  // The ref lets the effect close over a fresh config snapshot on
  // every render without declaring those fields as deps (which would
  // re-fire the check every keystroke and falsely flip stale state).
  const loadDriftCheckConfig = useRef(config);
  loadDriftCheckConfig.current = config;

  // Set baseline config hash when charges load from server so taint
  // detection works without requiring a manual generate first.
  // Also runs the loaded-vs-form drift check — when the loaded charges
  // disagree with the line item the form is bound to (legacy divergent
  // state from before the BE pricing-mode-change guard existed),
  // surface it through loadedChargesAreStale so the drawer locks Save
  // until the user regenerates.
  useEffect(() => {
    if (chargesLoaded && generationHash === null && localCharges.length > 0) {
      setGenerationHash(configHash);
      const matches = loadedChargesMatchFormConfig(
        localCharges,
        loadDriftCheckConfig.current
      );
      if (!matches) {
        setLoadedChargesAreStale(true);
      }
    }
  }, [chargesLoaded, generationHash, localCharges, configHash]);

  // Re-denominate loaded charges when the line item currency changes
  // mid-session. A currency change is a restamp (amounts, dates, labels, and
  // splits are preserved) rather than a schedule regenerate; marking the
  // schedule modified sends the charges so the new ISO persists and is
  // audited per charge. An amount the new currency cannot carry is kept and
  // surfaced through unrepresentableCharge, which blocks the save. While
  // charges are not loaded the baseline is re-seeded, so opening or switching
  // line items never counts as a change.
  useEffect(() => {
    if (!chargesLoaded) {
      prevCurrencyCodeRef.current = currencyCode;
      return;
    }
    if (prevCurrencyCodeRef.current === currencyCode) return;
    prevCurrencyCodeRef.current = currencyCode;
    if (localCharges.length === 0) return;
    setLocalCharges((prev) =>
      prev.map((charge) => ({
        ...charge,
        unitPrice:
          charge.unitPrice === null
            ? null
            : { ...charge.unitPrice, isoCode: currencyCode },
        totalPrice: { ...charge.totalPrice, isoCode: currencyCode },
      }))
    );
    setChargesModified(true);
  }, [currencyCode, chargesLoaded, localCharges.length]);

  // Reset charge state when line item changes
  useEffect(() => {
    setLocalCharges([]);
    setChargesModified(false);
    setChargesLoaded(false);
    setIsChargeEditing(false);
    setGenerationHash(null);
    setLoadedChargesAreStale(false);
  }, [lineItemKey]);

  // Pure compute of the reconciled charge list `handleGenerateCharges`
  // would persist — internal helper, no state mutation. Returns null
  // when [start, end] is missing and the generator can't run.
  // Coupled with the server's `generate` flag: generateChargesForLineItem in
  // asset-manager's src/modules/project/charge_generate.go builds the same
  // input from a stored line item. It refuses a missing field where this
  // falls back to a default. Change both together.
  const computeRegeneratedCharges = useCallback(():
    | ChargeFormValue[]
    | null => {
    if (!lineItemStartOn || !lineItemEndOn) return null;
    const invoicedCharges = localCharges.filter((c) => c.status === 'INVOICED');
    const chargeActivationDate = isUsableActivationDate(activationDate)
      ? activationDate
      : lineItemStartOn;
    const generated = generateCharges({
      startOn: lineItemStartOn,
      endOn: lineItemEndOn,
      chargeFrequency: chargeFrequency ?? 'MONTHLY',
      invoiceFrequency: invoiceFrequency ?? 'MONTHLY',
      invoiceMode: invoiceMode ?? 'ADVANCE',
      pricingMode: pricingMode,
      quantity: quantity,
      unitPrice: unitPriceNum,
      fixedPrice: fixedPriceNum,
      totalOverride: totalOverrideNum,
      chargeSchedule: chargeSchedule ?? 'PERIODIC',
      chargeMode: chargeMode,
      activationDate: chargeActivationDate,
      milestonePhases: stableMilestonePhases,
      currencyCode,
      preservedCharges:
        invoicedCharges.length > 0 ? invoicedCharges : undefined,
    });
    return reconcileChargeIds(
      generated,
      localCharges,
      chargeSchedule ?? 'PERIODIC'
    );
  }, [
    localCharges,
    lineItemStartOn,
    lineItemEndOn,
    chargeFrequency,
    invoiceFrequency,
    invoiceMode,
    pricingMode,
    quantity,
    unitPriceNum,
    fixedPriceNum,
    totalOverrideNum,
    chargeSchedule,
    stableMilestonePhases,
    chargeMode,
    activationDate,
    currencyCode,
  ]);

  const handleGenerateCharges = useCallback(() => {
    const reconciled = computeRegeneratedCharges();
    if (reconciled === null) return;

    // If reconciled charges are identical (same IDs and fields), treat as no-op
    const isNoOp =
      reconciled.length === localCharges.length &&
      reconciled.every(
        (c, i) =>
          c.id === localCharges[i].id && chargeFieldsMatch(c, localCharges[i])
      );
    if (isNoOp) {
      setGenerationHash(configHash);
      setLoadedChargesAreStale(false);
      return;
    }

    setLocalCharges(reconciled);
    setChargesModified(true);
    setIsChargeEditing(false);
    setGenerationKey((prev) => prev + 1);
    setGenerationHash(configHash);
    setLoadedChargesAreStale(false);
  }, [computeRegeneratedCharges, localCharges, configHash]);

  const handleUpdateCharge = useCallback(
    (index: number, updated: ChargeFormValue) => {
      setLocalCharges((prev) => {
        const next = [...prev];
        next[index] = updated;
        return next;
      });
      setChargesModified(true);
    },
    []
  );

  const handleDeleteCharge = useCallback((index: number) => {
    setLocalCharges((prev) => prev.filter((_, i) => i !== index));
    setChargesModified(true);
  }, []);

  const handleSplitCharge = useCallback((index: number) => {
    setLocalCharges((prev) => splitCharge(prev, index));
    setChargesModified(true);
  }, []);

  const handleAddCharge = useCallback((charge: ChargeFormValue) => {
    setLocalCharges((prev) => [...prev, charge]);
    setChargesModified(true);
  }, []);

  const resetChargesModified = useCallback(() => {
    setChargesModified(false);
  }, []);

  const resetState = useCallback(() => {
    setLocalCharges([]);
    setChargesModified(false);
    setChargesLoaded(false);
    setIsChargeEditing(false);
    setGenerationHash(null);
    setLoadedChargesAreStale(false);
  }, []);

  // Reset all charge state when the drawer closes
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  const unrepresentableCharge = findUnrepresentableCharge(localCharges);

  const isConfigOutOfSync =
    loadedChargesAreStale ||
    (generationHash !== null && generationHash !== configHash);

  return {
    localCharges,
    chargesModified,
    chargesLoaded,
    isChargeEditing,
    generationKey,
    isConfigOutOfSync,
    unrepresentableCharge,
    setIsChargeEditing,
    resetChargesModified,
    resetState,
    handleGenerateCharges,
    handleUpdateCharge,
    handleDeleteCharge,
    handleSplitCharge,
    handleAddCharge,
  };
}
